import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail, MessageSquareText, Phone } from "lucide-react";
import { CONDITIONS, EMAIL, MAILTO, PHONE_DISPLAY, PHONE_TEL, QUOTE_SMS_BODY, smsLink, VEHICLE_TYPES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  phone: "",
  email: "",
  vehicle_year: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_type: "",
  location: "",
  condition: "",
  preferred_date: "",
  message: "",
  company: "",
};

const Field = ({ label, required, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="field-label">
      {label} {required && <span className="text-blue-500">*</span>}
    </label>
    {children}
  </div>
);

const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, vehicle_type: e.detail }));
    window.addEventListener("prefill-vehicle", onPrefill);
    return () => window.removeEventListener("prefill-vehicle", onPrefill);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const res = await fetch(`${API}/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong sending your request.");
      }
      setStatus("success");
      toast.success("Quote request sent.");
    } catch (err) {
      setStatus("error");
      setError(
        typeof err.message === "string" && err.message.length < 140
          ? err.message
          : "We couldn't send your request. Please try again, or text us directly."
      );
      toast.error("Couldn't send your request.");
    }
  };

  return (
    <section id="contact" className="border-y border-white/10 bg-surface" data-testid="quote-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 py-24 sm:px-8 sm:py-32 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
        <div>
          <SectionHeading
            index="08"
            eyebrow="Contact"
            title="Get a Free Quote"
            sub="Tell us about your vehicle and we'll get back to you with pricing and availability."
          />
          <Reveal delay={0.15}>
            <div className="space-y-4">
              <a href={PHONE_TEL} className="flex items-center gap-4 border border-white/10 bg-obsidian p-5 transition-colors hover:border-blue-500/50" data-testid="contact-phone-link">
                <Phone size={20} strokeWidth={1.8} className="text-blue-500" aria-hidden="true" />
                <span>
                  <span className="block font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Call or Text</span>
                  <span className="mt-1 block font-display text-xl font-bold tracking-wide text-white">{PHONE_DISPLAY}</span>
                </span>
              </a>
              <a href={MAILTO} className="flex items-center gap-4 border border-white/10 bg-obsidian p-5 transition-colors hover:border-blue-500/50" data-testid="contact-email-link">
                <Mail size={20} strokeWidth={1.8} className="text-blue-500" aria-hidden="true" />
                <span>
                  <span className="block font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Email</span>
                  <span className="mt-1 block break-all font-display text-xl font-bold tracking-wide text-white">{EMAIL}</span>
                </span>
              </a>
              <div className="border border-white/10 bg-obsidian p-5">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">Prefer to text?</p>
                <a href={smsLink(QUOTE_SMS_BODY)} className="btn-outline mt-3 w-full !py-3 text-base" data-testid="contact-text-button">
                  <MessageSquareText size={16} strokeWidth={2.2} />
                  Text {PHONE_DISPLAY}
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} y={36}>
          <div id="quote" className="border border-white/10 bg-panel p-6 sm:p-10" data-testid="quote-form-card">
            {status === "success" ? (
              <div className="flex min-h-[420px] flex-col items-center justify-center text-center" data-testid="quote-success-message">
                <CheckCircle2 size={44} strokeWidth={1.5} className="text-blue-500" aria-hidden="true" />
                <h3 className="mt-6 font-display text-3xl font-bold uppercase tracking-wide text-white">Request Received</h3>
                <p className="mt-3 max-w-md text-base leading-relaxed text-slate-300">
                  Thank you! Your quote request has been received. We'll get back to you as soon as possible.
                </p>
                <p className="mt-2 max-w-md text-sm text-slate-400">
                  A confirmation is on its way to your email — you can reply to it anytime to add details.
                </p>
                <a href={smsLink(QUOTE_SMS_BODY)} className="btn-outline mt-8" data-testid="success-text-button">
                  <MessageSquareText size={16} strokeWidth={2.2} />
                  Prefer to text? Text {PHONE_DISPLAY}
                </a>
              </div>
            ) : (
              <form onSubmit={submit} noValidate={false} data-testid="quote-form">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Full Name" required htmlFor="full_name">
                    <input id="full_name" required maxLength={100} value={form.full_name} onChange={set("full_name")} className="field-input" autoComplete="name" data-testid="form-full-name" />
                  </Field>
                  <Field label="Phone Number" required htmlFor="phone">
                    <input id="phone" type="tel" required maxLength={25} value={form.phone} onChange={set("phone")} className="field-input" autoComplete="tel" data-testid="form-phone" />
                  </Field>
                  <Field label="Email Address" required htmlFor="email">
                    <input id="email" type="email" required maxLength={120} value={form.email} onChange={set("email")} className="field-input" autoComplete="email" data-testid="form-email" />
                  </Field>
                  <Field label="Service Address / Town" required htmlFor="location">
                    <input id="location" required maxLength={150} value={form.location} onChange={set("location")} className="field-input" placeholder="e.g. Gap, PA" autoComplete="street-address" data-testid="form-location" />
                  </Field>
                  <Field label="Vehicle Year" required htmlFor="vehicle_year">
                    <input id="vehicle_year" required maxLength={10} value={form.vehicle_year} onChange={set("vehicle_year")} className="field-input" placeholder="e.g. 2019" inputMode="numeric" data-testid="form-vehicle-year" />
                  </Field>
                  <Field label="Vehicle Make" required htmlFor="vehicle_make">
                    <input id="vehicle_make" required maxLength={50} value={form.vehicle_make} onChange={set("vehicle_make")} className="field-input" placeholder="e.g. Ford" data-testid="form-vehicle-make" />
                  </Field>
                  <Field label="Vehicle Model" required htmlFor="vehicle_model">
                    <input id="vehicle_model" required maxLength={50} value={form.vehicle_model} onChange={set("vehicle_model")} className="field-input" placeholder="e.g. F-150" data-testid="form-vehicle-model" />
                  </Field>
                  <Field label="Vehicle Type" required htmlFor="vehicle_type">
                    <select id="vehicle_type" required value={form.vehicle_type} onChange={set("vehicle_type")} className="field-input appearance-none" data-testid="form-vehicle-type">
                      <option value="" disabled>Select vehicle type</option>
                      {VEHICLE_TYPES.map((t) => (
                        <option key={t} value={t} className="bg-panel">{t}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Vehicle Condition" required htmlFor="condition">
                    <select id="condition" required value={form.condition} onChange={set("condition")} className="field-input appearance-none" data-testid="form-condition">
                      <option value="" disabled>Select condition</option>
                      {CONDITIONS.map((c) => (
                        <option key={c} value={c} className="bg-panel">{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Preferred Service Date" htmlFor="preferred_date">
                    <input id="preferred_date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className="field-input [color-scheme:dark]" data-testid="form-preferred-date" />
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Message / Additional Details" htmlFor="message">
                    <textarea id="message" rows={4} maxLength={2000} value={form.message} onChange={set("message")} className="field-input resize-y" data-testid="form-message" />
                  </Field>
                  <p className="mt-2 text-xs text-slate-500">
                    Tell us about stains, pet hair, work-related dirt, spills, or anything else we should know.
                  </p>
                </div>
                <div className="absolute left-[-9999px] top-0" aria-hidden="true">
                  <label htmlFor="company">Company</label>
                  <input id="company" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} />
                </div>
                {status === "error" && (
                  <p className="mt-5 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert" data-testid="quote-error-message">
                    {error} You can also text us directly at {PHONE_DISPLAY}.
                  </p>
                )}
                <button type="submit" disabled={status === "sending"} className="btn-primary mt-7 w-full disabled:cursor-not-allowed disabled:opacity-60" data-testid="form-submit-button">
                  {status === "sending" ? (
                    <>
                      <Loader2 size={17} className="animate-spin" aria-hidden="true" />
                      Sending…
                    </>
                  ) : status === "error" ? (
                    "Try Again"
                  ) : (
                    "Get My Free Quote"
                  )}
                </button>
                <p className="mt-4 text-center text-xs leading-relaxed text-slate-500">
                  Your information is only used to respond to your quote request. See our privacy policy for details.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default QuoteForm;
